import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getWeeklyReport, type WeeklyReport } from '../api/reports';

function ChangeIndicator({ value }: { value: number }) {
  if (value === 0) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>--</span>;
  const isUp = value > 0;
  const color = isUp ? 'var(--accent-red)' : 'var(--accent-green)';
  const arrow = isUp ? '\u2191' : '\u2193';
  return (
    <span style={{ color, fontSize: 12, fontWeight: 600 }}>
      {arrow} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

export function ReportsView() {
  const { getJwtToken } = useAuth();
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await getWeeklyReport(token, weekOffset);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken, weekOffset]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatCurrency = (val: number) =>
    `$${(val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatNumber = (val: number) => (val ?? 0).toLocaleString();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Weekly Report</h1>
        <div className="view__actions">
          <button
            className="btn btn--secondary"
            onClick={() => setWeekOffset((w) => w + 1)}
            type="button"
          >
            Previous Week
          </button>
          {weekOffset > 0 && (
            <button
              className="btn btn--secondary"
              onClick={() => setWeekOffset((w) => Math.max(0, w - 1))}
              type="button"
            >
              Next Week
            </button>
          )}
          <button className="btn btn--primary" onClick={handlePrint} type="button">
            Download as PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && !report && (
        <div className="view__empty">Generating report...</div>
      )}

      {report && (
        <>
          {/* Period Indicator */}
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Period: {report.period.start} to {report.period.end}
            {weekOffset === 0 ? ' (current week)' : ` (${weekOffset} week${weekOffset > 1 ? 's' : ''} ago)`}
          </p>

          {/* KPI Cards */}
          <div className="dashboard-kpis">
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {formatCurrency(report.totalSpend)}
              </span>
              <span className="dashboard-kpi__label">
                Total Spend <ChangeIndicator value={report.spendChangePercent} />
              </span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {formatCurrency(report.previousWeekSpend)}
              </span>
              <span className="dashboard-kpi__label">Previous Week</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {formatNumber(report.totalRequests)}
              </span>
              <span className="dashboard-kpi__label">Total Requests</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {formatNumber(report.totalTokens)}
              </span>
              <span className="dashboard-kpi__label">Total Tokens</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {report.newUsersCount}
              </span>
              <span className="dashboard-kpi__label">New Users</span>
            </div>
          </div>

          {/* Top Users & Top Models */}
          <div className="dashboard-sections">
            <section className="dashboard-section">
              <h2 className="view__section-title">Top Users by Spend</h2>
              {report.topUsers.length === 0 ? (
                <div className="settings-card settings-card--empty">No usage data</div>
              ) : (
                <div className="data-table__wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="data-table__th">User</th>
                        <th className="data-table__th" style={{ textAlign: 'right' }}>Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.topUsers.map((u) => (
                        <tr key={u.user} className="data-table__tr">
                          <td className="data-table__td">{u.user}</td>
                          <td className="data-table__td" style={{ textAlign: 'right' }}>
                            {formatCurrency(u.spend)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="dashboard-section">
              <h2 className="view__section-title">Top Models by Spend</h2>
              {report.topModels.length === 0 ? (
                <div className="settings-card settings-card--empty">No usage data</div>
              ) : (
                <div className="data-table__wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="data-table__th">Model</th>
                        <th className="data-table__th" style={{ textAlign: 'right' }}>Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.topModels.map((m) => (
                        <tr key={m.model} className="data-table__tr">
                          <td className="data-table__td">{m.model}</td>
                          <td className="data-table__td" style={{ textAlign: 'right' }}>
                            {formatCurrency(m.spend)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>

          {/* New Users This Week */}
          {report.newUsers.length > 0 && (
            <section className="view__section">
              <h2 className="view__section-title">New Users This Week</h2>
              <div className="settings-card">
                {report.newUsers.map((u) => (
                  <div key={u.username} className="settings-card__row">
                    <span className="settings-card__label">{u.username}</span>
                    <span className="settings-card__value">{u.email}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
